/**
 * ui.tsx — shared UI primitives for the enrollment tunnel.
 *
 * Ported from the ACCESS Member Onboarding Survey design prototype
 * (src/components.jsx): paper canvas, forest ink, coral pill CTAs,
 * honey accents, card-style tappable rows, survey shell with progress bar.
 *
 * All controls keep native inputs for WCAG 2.1 AA (visually hidden where
 * a styled surface replaces them) and ≥44px tap targets.
 */

import type { JSX, ReactNode } from 'react';

// ─── Buttons ──────────────────────────────────────────────────────────────────

interface PrimaryBtnProps {
  children: ReactNode;
  type?: 'button' | 'submit';
  onClick?: (() => void) | undefined;
  disabled?: boolean | undefined;
  busy?: boolean | undefined;
  kind?: 'coral' | 'ink' | 'outline';
  ariaLabel?: string | undefined;
  /** Associates the button with a <form id> elsewhere in the DOM. */
  formId?: string | undefined;
}

export function PrimaryBtn({
  children,
  type = 'button',
  onClick,
  disabled,
  busy,
  kind = 'coral',
  ariaLabel,
  formId,
}: PrimaryBtnProps): JSX.Element {
  const kindClasses = {
    coral: 'bg-action-primary text-white shadow-cta hover:bg-action-primary-hover',
    ink: 'bg-forest text-white hover:bg-forest-90',
    outline: 'bg-transparent text-ink border-[1.5px] border-forest-10 hover:bg-forest-5',
  }[kind];

  return (
    <button
      type={type}
      form={formId}
      onClick={onClick}
      disabled={disabled}
      aria-busy={busy}
      aria-label={ariaLabel}
      className={`inline-flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 rounded-full px-6 text-base font-bold transition-[opacity,transform] duration-150 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none ${kindClasses}`}
    >
      {children}
    </button>
  );
}

// ─── Cards & chrome ───────────────────────────────────────────────────────────

const cardTones = {
  white: 'bg-white border-card-border shadow-card',
  honey: 'bg-honey-soft border-transparent',
  coral: 'bg-coral-soft border-transparent',
  forest5: 'bg-forest-5 border-transparent',
} as const;

export function Card({
  children,
  tone = 'white',
  className = '',
}: {
  children: ReactNode;
  tone?: keyof typeof cardTones;
  className?: string;
}): JSX.Element {
  return <div className={`rounded-2xl border p-4 ${cardTones[tone]} ${className}`}>{children}</div>;
}

/** Small uppercase status chip (prototype `Pill`). */
export function Pill({
  children,
  tone = 'coral',
}: {
  children: ReactNode;
  tone?: 'coral' | 'forest' | 'honey';
}): JSX.Element {
  const tones = {
    coral: 'bg-coral-soft text-coral',
    forest: 'bg-forest-10 text-forest',
    honey: 'bg-honey-soft text-ink',
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.3px] uppercase ${tones}`}
    >
      {children}
    </span>
  );
}

/** Uppercase eyebrow label (prototype landing `Eyebrow`). */
export function Eyebrow({
  children,
  className = 'text-coral-dark',
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={`text-[11.5px] font-extrabold tracking-[1.2px] uppercase ${className}`}>
      {children}
    </div>
  );
}

export function CheckIcon({ className = '' }: { className?: string }): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M5 12l5 5L20 7"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Form rows ────────────────────────────────────────────────────────────────

interface CheckboxRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  sublabel?: ReactNode;
  ariaLabel?: string | undefined;
}

/** Card-style checkbox row with a forest check square (prototype `CheckboxRow`). */
export function CheckboxRow({
  checked,
  onChange,
  label,
  sublabel,
  ariaLabel,
}: CheckboxRowProps): JSX.Element {
  return (
    <label
      className={`flex w-full cursor-pointer items-start gap-3 rounded-[14px] border-[1.5px] bg-white px-4 py-3.5 transition-colors duration-150 ${
        checked ? 'border-forest' : 'border-field-border'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={ariaLabel}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={`mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-[1.5px] ${
          checked ? 'border-forest bg-forest text-white' : 'border-field-border bg-white'
        }`}
      >
        {checked && <CheckIcon />}
      </span>
      <span className="flex-1">
        <span className="block text-sm leading-snug font-semibold text-ink">{label}</span>
        {sublabel && (
          <span className="mt-1 block text-xs leading-relaxed text-ink-soft">{sublabel}</span>
        )}
      </span>
    </label>
  );
}

interface RadioRowProps {
  name: string;
  value: string;
  checked: boolean;
  onChange: () => void;
  label: ReactNode;
}

/** Card-style radio row with a forest dot (prototype `RadioGroup` stack layout). */
export function RadioRow({ name, value, checked, onChange, label }: RadioRowProps): JSX.Element {
  return (
    <label
      className={`flex min-h-[44px] w-full cursor-pointer items-center gap-3 rounded-[14px] border-[1.5px] px-4 py-3.5 text-[15px] font-semibold transition-colors duration-150 ${
        checked ? 'border-forest bg-forest text-white' : 'border-field-border bg-white text-ink'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 ${
          checked ? 'border-white bg-forest' : 'border-field-border bg-white'
        }`}
      >
        {checked && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
      {label}
    </label>
  );
}

// ─── Survey shell ─────────────────────────────────────────────────────────────

interface SurveyShellProps {
  stepLabel: string;
  /** 0..1 progress bar fill. */
  progress: number;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Slot rendered right-aligned in the header (e.g. language toggle). */
  headerAction?: ReactNode;
}

/**
 * Brand bar shown above the desktop sheet (logo + program chip).
 * Hidden on mobile, where the tunnel is full-bleed.
 */
export function SheetBrandBar(): JSX.Element {
  return (
    <div className="hidden w-full max-w-[640px] items-center justify-between pb-5 lg:flex">
      <img src="/assets/logo-red.png" alt="NourishedRx" className="block h-6" />
      <span className="rounded-full bg-white px-3 py-1 text-[10.5px] font-extrabold tracking-[0.6px] uppercase text-steel">
        Medicare ACCESS
      </span>
    </div>
  );
}

/**
 * Desktop stage wrapper: full-bleed paper on mobile; at lg+ the content
 * renders as a centered sheet card on a dimmer paper stage (mirrors the
 * prototype's phone-on-stage framing).
 */
export function SheetStage({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="min-h-dvh bg-paper lg:flex lg:flex-col lg:items-center lg:bg-paper-dim lg:px-8 lg:py-10">
      <SheetBrandBar />
      {children}
    </div>
  );
}

/**
 * Survey screen frame (prototype `SurveyScreen`): sticky header with step
 * label + coral progress bar, scrollable body, sticky footer with the CTA.
 * On desktop the frame becomes a centered sheet card on the stage.
 */
export function SurveyShell({
  stepLabel,
  progress,
  title,
  sub,
  children,
  footer,
  headerAction,
}: SurveyShellProps): JSX.Element {
  return (
    <SheetStage>
      <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col bg-paper lg:min-h-0 lg:max-w-[640px] lg:flex-none lg:overflow-hidden lg:rounded-[28px] lg:border lg:border-card-border lg:shadow-pop">
        <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-forest-10 bg-paper px-4 pt-3 pb-3.5 sm:px-5 lg:static lg:px-8 lg:pt-6">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[11px] font-bold tracking-[0.6px] uppercase text-ink-soft">
                {stepLabel}
              </div>
              {headerAction}
            </div>
            <div
              className="mt-1.5 h-1 overflow-hidden rounded-sm bg-forest-10"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
              aria-label="Enrollment progress"
            >
              <div
                className="h-full rounded-sm bg-coral transition-[width] duration-400"
                style={{ width: `${Math.max(4, progress * 100)}%` }}
              />
            </div>
          </div>
        </header>

        <div className="flex-1 px-4 pt-4 pb-7 sm:px-5 lg:px-8 lg:pt-6">
          <h1 className="text-[22px] leading-tight font-bold tracking-tight text-ink lg:text-[26px]">
            {title}
          </h1>
          {sub && (
            <p className="mt-2 text-sm leading-relaxed text-ink-soft lg:text-[15px]">{sub}</p>
          )}
          <div className="mt-5 flex flex-col gap-3.5">{children}</div>
        </div>

        {footer && (
          <footer className="sticky bottom-0 border-t border-forest-10 bg-paper px-4 pt-3 pb-[max(1.125rem,env(safe-area-inset-bottom))] sm:px-5 lg:static lg:px-8 lg:pb-7">
            {footer}
          </footer>
        )}
      </main>
    </SheetStage>
  );
}

/**
 * Centered single-card status screen (holding pages, success states,
 * errors) on the paper canvas. On desktop, a centered sheet card.
 */
export function StatusScreen({
  icon,
  title,
  children,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="min-h-dvh bg-paper lg:flex lg:flex-col lg:items-center lg:justify-center lg:bg-paper-dim lg:px-8 lg:py-10">
      <SheetBrandBar />
      <main className="mx-auto flex min-h-dvh w-full max-w-lg flex-col items-center justify-center bg-paper px-5 py-10 lg:min-h-0 lg:max-w-[560px] lg:flex-none lg:rounded-[28px] lg:border lg:border-card-border lg:px-10 lg:py-14 lg:shadow-pop">
        {icon}
        <h1 className="text-center text-2xl leading-tight font-bold tracking-tight text-ink">
          {title}
        </h1>
        <div className="mt-3 flex w-full flex-col items-center gap-3 text-center text-sm leading-relaxed text-ink-soft">
          {children}
        </div>
      </main>
    </div>
  );
}

/** Large circular icon badge used on status/thank-you screens. */
export function IconBadge({
  tone = 'coral',
  children,
}: {
  tone?: 'coral' | 'honey' | 'forest';
  children: ReactNode;
}): JSX.Element {
  const tones = {
    coral: 'bg-coral-soft text-coral',
    honey: 'bg-honey-soft text-honey',
    forest: 'bg-forest-5 text-forest',
  }[tone];
  return (
    <div
      className={`mb-4 flex h-[76px] w-[76px] items-center justify-center rounded-full ${tones}`}
    >
      {children}
    </div>
  );
}
